import React from 'react';

/**
 * QuotedExternal — content written by someone OUTSIDE the business, shown to the
 * customer without Aidey vouching for it.
 *
 * WHY THIS EXISTS
 * Text from an inbound email, an emailed calendar invite, a portal message or a
 * public form never enters the model's instruction context (lib/externalText.js
 * redacts it from the prompt; tool results re-enter context, so it cannot be
 * fetched with a tool either). But the customer still has to be able to READ it,
 * or the product is worse than before the fix. This is the only route by which
 * that content reaches a screen.
 *
 * THE FOUR THINGS IT HAS TO DO
 *  1. Be obviously someone else's words — a quote rail, its own surface, and it
 *     is NOT a message bubble.
 *  2. Be obviously WHO — the envelope sender, never a display name the sender
 *     chose for themselves.
 *  3. Never look like Aidey endorsing or summarising. Aidey does not paraphrase
 *     this content; it says so.
 *  4. An unknown sender reads as UNKNOWN. Never omitted, never softened to
 *     "a client" or "someone".
 *
 * LINKS ARE DELIBERATELY NOT CLICKABLE.
 * A clickable link inside a block that says "I haven't checked this" is a
 * contradiction, and it re-opens the P1 phishing route through the back door:
 * an attacker emails the customer, and the product renders their URL as
 * one-click. Copying is the right amount of friction for something we are
 * explicitly not vouching for.
 *
 * Callers: Aidey Chat (M2) and the booking-link surface (P1).
 */
export default function QuotedExternal({ senderEmail, text, kind = 'message', receivedAt }) {
  const from = (senderEmail && String(senderEmail).trim()) || null;

  return (
    <div
      style={S.wrap}
      role="group"
      aria-label={`Quoted ${kind} from ${from || 'an unknown sender'}, shown unmodified and not verified`}
    >
      <div style={S.head}>
        <span style={S.fromLabel}>From:&nbsp;</span>
        {/* Requirement 4: unknown must READ as unknown. */}
        <span style={from ? S.from : S.fromUnknown}>{from || 'sender unknown'}</span>
        <span style={S.disclaimer}>, this is their {kind}, not Aidey&apos;s</span>
      </div>

      <div style={S.quote}>
        {/* Plain text only. No dangerouslySetInnerHTML, no linkification: the
            whole point is that nothing in here is actionable. */}
        <div style={S.body}>{text}</div>
      </div>

      <div style={S.footer}>
        <span>quoted exactly as received{receivedAt ? ` · ${receivedAt}` : ''}</span>
        <span style={S.notChecked}>I haven&apos;t checked this or acted on it.</span>
      </div>
    </div>
  );
}

// Block C: tokens only (src/ui/tokens.css). The four requirements above are unchanged: its own
// surface with a quote rail (not a bubble), the envelope sender, the disclaimer, and an unknown
// sender that reads as unknown.
const S = {
  wrap: {
    boxSizing: 'border-box', width: '100%',
    border: 'var(--border) solid var(--line)', borderRadius: 'var(--radius-button)', background: 'var(--surface-2)',
    padding: 'var(--space-3) var(--space-3-5)', maxWidth: 620,
    fontFamily: 'var(--font-body)', color: 'var(--ink)',
  },
  head: { display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 'var(--space-1)', fontSize: 'var(--text-badge)', marginBottom: 'var(--space-2)' },
  fromLabel: { color: 'var(--ink-3)' },
  from: { color: 'var(--ink)', fontWeight: 700, wordBreak: 'break-all' },
  fromUnknown: { color: 'var(--danger)', fontWeight: 700, fontStyle: 'italic' },
  disclaimer: { color: 'var(--ink-3)', fontStyle: 'italic', marginLeft: 'var(--space-1-5)' },
  quote: { borderLeft: '3px solid var(--line)', paddingLeft: 'var(--space-3)' },
  body: {
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    fontSize: 'var(--text-small)', lineHeight: 1.55, color: 'var(--ink-2)',
    fontFamily: "'SFMono-Regular', Consolas, monospace",
  },
  footer: {
    marginTop: 'var(--space-2)', display: 'flex', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 'var(--space-1-5)', fontSize: 'var(--text-badge)', color: 'var(--ink-4)',
  },
  notChecked: { fontStyle: 'italic' },
};
